import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { CheckCircle2, Calendar, Users, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicAttendancePage() {
  const { publicCode } = useParams();
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [itsId, setItsId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchEventInfo();
  }, [publicCode]);

  // Auto-submit when 8 digits are entered
  useEffect(() => {
    if (itsId.length === 8 && /^\d{8}$/.test(itsId)) {
      handleMarkAttendance();
    }
  }, [itsId]);

  const fetchEventInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/attendance/${publicCode}/info`);
      setEventInfo(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid attendance link');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendance = async () => {
    if (submitting) return;
    if (!itsId || itsId.length !== 8 || !/^\d{8}$/.test(itsId)) {
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/public/attendance/${publicCode}?its_id=${itsId}`);
      setResult(response.data);
      
      // Clear input immediately for next person
      setItsId('');
      inputRef.current?.focus();
      
      // Clear result message after 1.5 seconds
      setTimeout(() => {
        setResult(null);
      }, 1500);
      
    } catch (err) {
      setResult({
        success: false,
        message: err.response?.data?.detail || 'Failed to mark attendance'
      });
      // Clear error after 2 seconds
      setTimeout(() => {
        setItsId('');
        setResult(null);
        inputRef.current?.focus();
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleItsChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setItsId(value);
    setResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-10 h-10 border-3 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Invalid Link</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!eventInfo?.is_active) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Event Closed</h2>
            <p className="text-slate-500">This attendance event is no longer active.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy/5 to-slate-100 flex flex-col items-center justify-center p-4" data-testid="public-attendance-page">
      {/* Event Info */}
      <Card className="w-full max-w-md border-0 shadow-xl mb-4">
        <CardHeader className="text-center pb-2 bg-navy text-white rounded-t-lg">
          <CardTitle className="text-lg font-heading">{eventInfo?.name}</CardTitle>
          <div className="flex items-center justify-center gap-4 text-sm opacity-90">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {eventInfo?.event_date}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {eventInfo?.total_attendance} attended
            </span>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* ITS Input */}
          <div className="text-center">
            <label className="text-sm text-slate-600 mb-2 block">Enter your ITS Number</label>
            <Input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={itsId}
              onChange={handleItsChange}
              placeholder="12345678"
              className="text-center text-2xl font-mono tracking-[0.5em] h-14 border-2 focus:border-navy"
              maxLength={8}
              autoFocus
              disabled={submitting}
              data-testid="its-input"
            />
            <p className="text-xs text-slate-400 mt-2">
              {itsId.length}/8 digits {itsId.length === 8 && '- Auto submitting...'}
            </p>
          </div>

          {/* Loading */}
          {submitting && (
            <div className="flex items-center justify-center gap-2 text-navy">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Recording attendance...</span>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-lg ${
              result.success 
                ? result.already_marked 
                  ? 'bg-amber-50 border border-amber-200' 
                  : 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {result.success ? (
                  <CheckCircle2 className={`w-6 h-6 flex-shrink-0 ${result.already_marked ? 'text-amber-500' : 'text-green-500'}`} />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${
                    result.success 
                      ? result.already_marked ? 'text-amber-700' : 'text-green-700'
                      : 'text-red-700'
                  }`}>
                    {result.message}
                  </p>
                  {result.success && result.member_name && (
                    <p className="text-sm text-slate-600 mt-1">
                      Welcome, <span className="font-semibold">{result.member_name}</span>!
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          {!result && !submitting && (
            <div className="text-center text-sm text-slate-500 space-y-1">
              <p>Attendance will be recorded automatically</p>
              <p>when you enter your complete 8-digit ITS number</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-xs text-slate-400 text-center">
        Powered by Anjuman Management System
      </p>
    </div>
  );
}
